import * as THREE from 'three';
import { canvasElementTypes } from '@/constants/canvas';
import { ICanvasElement } from '@/interface/canvas';
import { getImageDimension } from '@/util/image';
import { gameCategory } from '@/constants/game';

const textMesh = (element: ICanvasElement) => {
  const text = document.createElement('a-text');
  const position = element?.position || [0, 0, 0];
  const newPosition = [...position];
  text.setAttribute('value', element?.text || '');
  text.setAttribute('color', element?.color || 'gray');
  text.setAttribute(
    'opacity',
    element?.opacity != undefined ? element.opacity.toString() : '1'
  );
  text.setAttribute('position', newPosition?.join(' '));
  text.setAttribute('scale', element.scale?.join(' '));
  text.setAttribute(
    'rotation',
    element.rotation.map(r => THREE.MathUtils.radToDeg(r))?.join(' ')
  );
  text.setAttribute('side', 'double');
  text.setAttribute('anchor', 'center');
  text.setAttribute('align', 'center');

  // text.setAttribute('wrap-count', '20');
  text.setAttribute('mixin', 'text');
  text.setAttribute('width', '22');

  if (element?.cta_uri) {
    text.setAttribute('class', 'clickable');
    text.addEventListener('click', () => {
      window.open(element?.cta_uri, '_blank');
    });
  }
  return text;
};

const glb3DMesh = (element: ICanvasElement) => {
  const entity = document.createElement('a-entity');
  entity.setAttribute('gltf-model', `url(${element?.uri || ''})`);
  entity.setAttribute('animation-mixer', 'clip: *; loop: repeat');
  entity.setAttribute('position', element.position?.join(' '));
  entity.setAttribute('scale', element.scale?.join(' '));
  entity.setAttribute(
    'rotation',
    element.rotation.map(r => THREE.MathUtils.radToDeg(r))?.join(' ')
  );
  entity.setAttribute('transparent', 'true');
  if (element?.cta_uri) {
    entity.setAttribute('class', 'clickable');
    entity.addEventListener('click', () => {
      window.open(element?.cta_uri, '_blank');
    });
  }
  return entity;
};

const imageMesh = async (element: ICanvasElement) => {
  const image = document.createElement('a-plane');

  if (element?.uri) {
    const dimension: any = await getImageDimension(element.uri);

    dimension?.height && image.setAttribute('height', dimension.height);
    dimension?.width && image.setAttribute('width', dimension.width);
  }
  image.setAttribute('src', `${element?.uri}?date=2929`);
  //   image.setAttribute('color', element?.color || '');
  image.setAttribute('position', element.position?.join(' '));
  image.setAttribute('scale', element.scale?.join(' '));
  image.setAttribute(
    'rotation',
    element.rotation.map(r => THREE.MathUtils.radToDeg(r))?.join(' ')
  );
  image.setAttribute('transparent', 'true');
  image.setAttribute('side', 'double');
  image.setAttribute('depth-write', 'false');
  if (element?.curv_x || element?.curv_y)
    image.setAttribute(
      'curvator',
      `curvatureX: ${element?.curv_x || 0}; curvatureY: ${
        element?.curv_y || 0
      };`
    );

  image.setAttribute(
    'opacity',
    element?.opacity != undefined ? element.opacity.toString() : '1'
  );
  if (element?.cta_uri) {
    image.setAttribute('class', 'clickable');
    image.addEventListener('click', () => {
      window.open(element?.cta_uri, '_blank');
    });
  }
  return image;
};

const videoMesh = async (element: ICanvasElement, isClickEvent = true) => {
  if (!element?.uri) return;
  const autoplay = element?.auto_play || false;
  const video = document.createElement('video');
  const videoEntity = document.createElement('a-entity');
  const thumbnailEntity = document.createElement('a-entity');
  const videoScreenEntity = document.createElement('a-entity');
  const playBtnPlane = document.createElement('a-plane');

  video.setAttribute('preload', 'auto');
  video.setAttribute('crossorigin', 'anonymous');
  video.setAttribute('src', element?.uri);
  video.setAttribute('playsinline', 'true');
  video.setAttribute('webkit-playsinline', 'true');
  video.setAttribute('id', `video_${element?._id}`);
  video.setAttribute('visible', 'false');
  video.setAttribute('loop', 'true');
  video.setAttribute('video_autoplay', `${autoplay}`);
  video.setAttribute('video_entity_id', `video_entity_${element?._id}`);

  const sceneAssets = document.querySelector('a-assets');
  sceneAssets?.appendChild(video);

  videoEntity.setAttribute('position', element.position?.join(' '));
  videoEntity.setAttribute('scale', element.scale?.join(' '));
  videoEntity.setAttribute(
    'rotation',
    element.rotation.map(r => THREE.MathUtils.radToDeg(r))?.join(' ')
  );
  videoEntity.setAttribute('id', `video_entity_${element?._id}`);

  videoScreenEntity.setAttribute('material', `src:#video_${element?._id}`);
  videoScreenEntity.setAttribute('position', '0 0 0');
  videoScreenEntity.setAttribute(
    'geometry',
    'primitive: plane; height: 1; width: 1.78; color: blue;'
  );
  videoScreenEntity.setAttribute('id', 'video_screen');
  videoScreenEntity.setAttribute('video_id', `video_${element?._id}`);
  videoScreenEntity.setAttribute(
    'video_entity_id',
    `video_entity_${element?._id}`
  );
  videoScreenEntity.setAttribute('class', 'clickable');

  if (element?.thumbnail) {
    thumbnailEntity.setAttribute(
      'geometry',
      'primitive: plane; height: 1; width: 1.78'
    );
    thumbnailEntity.setAttribute('position', '0 0 0');
    thumbnailEntity.setAttribute('scale', '1 1 1');
    thumbnailEntity.setAttribute(
      'material',
      `src:${element?.thumbnail}?date=993939`
    );
    thumbnailEntity.setAttribute('id', `thumb_${element?._id}`);

    const dimension: any = await getImageDimension(element.thumbnail);
    videoScreenEntity.setAttribute(
      'geometry',
      `primitive: plane; height: ${dimension.height}; width: ${dimension.width}; color: blue;`
    );
    thumbnailEntity.setAttribute(
      'geometry',
      `primitive: plane; height: ${dimension.height}; width: ${dimension.width}`
    );
    thumbnailEntity.setAttribute('class', 'video-thumbnail');
  }

  playBtnPlane.setAttribute(
    'src',
    'https://rhym.s3.ap-south-1.amazonaws.com/website_assets/AR/static/images/vidplay.png'
  );
  playBtnPlane.setAttribute('position', '0 0 0.01');
  playBtnPlane.setAttribute('transparent', 'true');
  playBtnPlane.setAttribute('id', element?._id || '');
  playBtnPlane.setAttribute('class', 'clickable play-btn');
  // playBtnPlane.setAttribute('video-play', '');
  playBtnPlane.setAttribute('height', `0.5`);
  playBtnPlane.setAttribute('width', `0.5`);
  playBtnPlane.setAttribute('video_id', `video_${element?._id}`);
  playBtnPlane.setAttribute('video_entity_id', `video_entity_${element?._id}`);

  video.onplay = () => {
    playBtnPlane.setAttribute('visible', 'false');
    thumbnailEntity.setAttribute('visible', 'false');
  };
  video.onpause = () => {
    playBtnPlane.setAttribute('visible', 'true');
    thumbnailEntity.setAttribute('visible', 'true');
  };
  if (isClickEvent) {
    playBtnPlane.addEventListener('click', () => {
      const isPaused = video.paused;
      if (isPaused) {
        video.play();
      } else {
        video.pause();
      }
    });
    videoScreenEntity.addEventListener('click', () => {
      const isPaused = video.paused;

      if (!isPaused) {
        video.pause();
      } else {
        video.play();
      }
    });
  }

  videoEntity.appendChild(videoScreenEntity);
  videoEntity.appendChild(thumbnailEntity);
  videoEntity.appendChild(playBtnPlane);

  return videoEntity;
};

export const renderARElements = async (
  elements: Array<ICanvasElement>,
  sceneGroupId = 'entity_elements',
  category = ''
) => {
  const elementsEntity: any = document.getElementById(sceneGroupId);
  if (!elementsEntity) return;
  elements.forEach(async ele => {
    const element = { ...ele };
    element.position = ele.position || [];
    element.rotation = ele.rotation || [];
    element.scale = ele.scale || [];
    if (ele?.type === canvasElementTypes.TEXT) {
      const text = textMesh(element);
      if (category === gameCategory.MARKERLESS) {
        text.setAttribute('gesture-handler', '');
      }
      elementsEntity.appendChild(text);
    } else if (
      ele?.type === canvasElementTypes.GLTF ||
      ele?.type === canvasElementTypes.GLB
    ) {
      const entity = glb3DMesh(element);
      if (category === gameCategory.MARKERLESS) {
        entity.setAttribute('gesture-handler', '');
      }
      elementsEntity.appendChild(entity);
    } else if (ele?.type === canvasElementTypes.IMAGE) {
      const image = await imageMesh(element);
      if (category === gameCategory.MARKERLESS) {
        image.setAttribute('gesture-handler', '');
      }
      elementsEntity.appendChild(image);
    } else if (ele?.type === canvasElementTypes.VIDEO) {
      const isClickEvent = category !== 'MARKERBASED';
      const video = await videoMesh(element, isClickEvent);
      if (category === gameCategory.MARKERLESS && video) {
        video.setAttribute('gesture-handler', '');
      }
      video && elementsEntity.appendChild(video);
    }
    const cursor = document.getElementById('cursor');
    if (cursor) {
      cursor.setAttribute('visible', 'true');
      cursor.setAttribute('tap-cursor', '');
    }
  });
};

export const renderARMarker = (pattUri: string) => {
  const scene = document.getElementById('scene');
  const marker = document.createElement('a-marker');
  const webstageEntity = document.createElement('a-entity');
  const elemetsEnetity = document.createElement('a-entity');
  const box = document.createElement('a-box');
  box.setAttribute('position', '0 0 0');
  box.setAttribute('material', 'color: yellow;');

  marker.setAttribute('type', 'pattern');
  marker.setAttribute('url', pattUri);
  marker.setAttribute('markerhandler', '');
  // marker.setAttribute('raycaster', 'objects: none');
  marker.setAttribute('smooth', 'true');
  // marker.setAttribute('smooth-threshold', '8');
  marker.setAttribute('smooth-count', '10');

  marker.setAttribute('cursor', 'rayOrigin: mouse');
  // marker.setAttribute('raycaster', 'objects: .clickable');
  marker.setAttribute('emitevents', 'true');
  marker.setAttribute('video-click', '');
  // marker.setAttribute('preset', 'hiro');

  webstageEntity.setAttribute('id', 'webar_stage');
  webstageEntity.setAttribute('position', '0 0 0');

  elemetsEnetity.setAttribute('id', 'entity_elements');
  elemetsEnetity.setAttribute('position', '0 0 0');

  webstageEntity.appendChild(elemetsEnetity);

  marker.appendChild(webstageEntity);

  // marker.appendChild(box);
  scene && scene.appendChild(marker);
};

export const renderARImageTrackingMarker = async () => {
  const marker = document.getElementById('marker');

  const webstageEntity = document.createElement('a-entity');
  const elemetsEnetity = document.createElement('a-entity');

  webstageEntity.setAttribute('id', 'webar_stage');
  webstageEntity.setAttribute('position', '0 0 0');
  webstageEntity.setAttribute('scale', '0.25 0.25 0.25');

  elemetsEnetity.setAttribute('id', 'entity_elements');
  elemetsEnetity.setAttribute('position', '0 0 0');

  webstageEntity.appendChild(elemetsEnetity);
  if (marker) {
    marker.setAttribute('image-markerhandler', '');
    marker.appendChild(webstageEntity);
  }
};
